import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map, take, tap, Subscription } from 'rxjs';
import { PortalsService } from '../../../services/portals.service';
import { PermissionsService } from '../../../services/permissions.service';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton, MatAnchor } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { MatSelect, MatOption, MatSelectModule  } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  QuickSelectDateOptions,
  defaultQuickSelectDate,
  getQuickSelectDateOption
} from '../../../../../../../libs/data/analytics';
import { CommonModule } from '@angular/common';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'career-scope-hq-portal-search',
    templateUrl: './hq-portal-search.component.html',
    styleUrls: ['./hq-portal-search.component.scss'],
    providers: [provideNativeDateAdapter()],
    imports: [
        CommonModule,
        MatFormField,
        MatInput,
        FormsModule,
        ReactiveFormsModule,
        MatIcon,
        MatPrefix,
        NgIf,
        MatIconButton,
        MatSuffix,
        MatButton,
        MatAnchor,
        RouterLink,
        AsyncPipe,
        MatSelect,
        MatOption,
        MatSelectModule,
        MatDatepickerModule,
        MatDialogModule,
        MatProgressSpinnerModule,
    ]
})
export class HqPortalSearchComponent implements OnInit, OnDestroy {

  quickSelectDate = QuickSelectDateOptions[0].value;
  quickSelectDateOptions = QuickSelectDateOptions;

  startDate!: Date;
  endDate!: Date;

  permissions = inject(PermissionsService);
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);

  searchControl = new FormControl<string | null>(null, { nonNullable: false });
  searchControlSubscription!: Subscription;

  viewDeveloperContent$ = this.permissions.hasRole('ViewDeveloperContent');
  /** CSE-122 — FIT Score toggle button visible only to HQ Admin users (hqAccess). */
  isHqAdmin$ = this.auth.user$.pipe(map(user => !!user?.hqAccess));

  /**
   * null  = state not yet loaded (spinner shown)
   * false = not all portals have fitScore:true → button shows "Enable FIT Score (All)"
   * true  = all portals have fitScore:true    → button shows "Disable FIT Score (All)"
   */
  fitScoreAllEnabled: boolean | null = null;
  fitScoreLoading = false;

  constructor(private pss: PortalsService, private notif: NotificationService) { }

  ngOnInit(): void {
    this.searchControl.setValue(this.pss.searchValue.value, { emitEvent: false });
    this.searchControlSubscription = this.searchControl.valueChanges.pipe(
      tap(value => {
        this.pss.searchValue.next(value);
      })
    ).subscribe();

    // Load the real FIT Score state once on init so the button label is accurate
    this.isHqAdmin$.pipe(take(1)).subscribe(async isAdmin => {
      if (!isAdmin) return;
      try {
        this.fitScoreAllEnabled = await this.pss.checkFitScoreAllEnabled();
      } catch {
        this.fitScoreAllEnabled = false; // safe default
      }
    });
  }

  rebuildDashboard() {
    this.pss.rebuildHQDashboard();
  }

  /**
   * CSE-122 — single toggle button: reads current `fitScoreAllEnabled` to decide
   * direction, shows a confirmation dialog, runs the bulk update, then flips the state.
   */
  toggleFitScoreAllPortals() {
    const enable = !this.fitScoreAllEnabled;
    const action = enable ? 'Enable' : 'Disable';

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: `${action} FIT Score on All Portals`,
        message: enable
          ? 'This will set fitScore: true on every live and demo portal (~500 portals). FIT Score features (recommendations, job family summary, O*NET query) will become active for all seekers. Are you sure?'
          : 'This will set fitScore: false on every live and demo portal. FIT Score features will be hidden for all seekers. Are you sure?',
        confirmText: `${action} FIT Score`,
        cancelText: 'Cancel',
        type: enable ? 'warning' : 'error',
      },
      width: '480px',
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      this.fitScoreLoading = true;
      try {
        const count = await this.pss.setFitScoreAllPortals(enable);
        this.fitScoreAllEnabled = enable; // flip the state on success
        this.notif.openSnackBar(
          `FIT Score ${enable ? 'enabled' : 'disabled'} on ${count} portals.`,
          'Dismiss',
          { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top' }
        );
      } catch (err) {
        console.error('setFitScoreAllPortals error', err);
        this.notif.openSnackBar(
          `Failed to ${action.toLowerCase()} FIT Score: ${(err as Error).message}`,
          'Dismiss',
          { duration: 6000, horizontalPosition: 'center', verticalPosition: 'top' }
        );
      } finally {
        this.fitScoreLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.searchControlSubscription?.unsubscribe();
  }

//   createUsageReport(): void {
//   const { startDate, endDate } = getQuickSelectDateOption(
//     this.quickSelectDate
//   );

//   // TODO: emit / call service / navigate
//   console.log('Usage report date range:', startDate, endDate);
  
// }
createUsageReport(): void {
  let startDate: Date | undefined;
  let endDate: Date | undefined;

  // 🔹 Resolve date range
  if (this.quickSelectDate === 'custom') {
    startDate = this.startDate;
    endDate = this.endDate;
  } else {
    const range = getQuickSelectDateOption(this.quickSelectDate);
    startDate = range.startDate;
    endDate = range.endDate;
  }

  if (!startDate || !endDate) {
    alert('Please select a valid date range');
    return;
  }

  // 🔹 Get portal data from existing HQ service
  const portals = this.pss.filteredPortals.value ?? [];

  // 🔹 Filter completed records in date range
  const rows = portals
    .filter(p =>
      p.completed > 0 &&
      p.lastCompletedDate &&
      new Date(p.lastCompletedDate) >= startDate! &&
      new Date(p.lastCompletedDate) <= endDate!
    )
    .map(p => ({
      'Customer': p.customerNumber,
      'Portal': p.name,
      'Completed Records': p.completed,
      'Start Date': this.formatDate(startDate),
      'End Date': this.formatDate(endDate)
    }));

  if (!rows.length) {
    alert('No completed records found for selected date range');
    return;
  }

  this.downloadCsv(rows);
}

private downloadCsv(rows: any[]): void {
  const headers = Object.keys(rows[0]).join(',');
  const csvData = rows.map(r =>
    Object.values(r)
      .map(v => `"${v ?? ''}"`)
      .join(',')
  );

  const csv = [headers, ...csvData].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `usage-report-${Date.now()}.csv`;
  link.click();

  URL.revokeObjectURL(url);
}

private formatDate(date?: Date): string {
  if (!date) return '';
  return new Date(date).toISOString().split('T')[0];
}


}
